import { NewAutoCompleteField } from "@components/form/AutocompleteField";
import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ReactComponent as FilterIcon } from "src/assets/icon/filter.svg";
import { ReactComponent as SearchIcon } from "src/assets/icon/search.svg";
import FilterMenu from "src/components/filter/FilterMenu";
import NewTextField from "src/components/form/TextField";
import { getTaxCategories } from "../constants";
import { IControllerProps } from "../types";

interface ITaxesFilterProps {
    loader: boolean;
    filterState: IControllerProps;
    setFilterState: React.Dispatch<React.SetStateAction<IControllerProps>>;
    taxCategories?: Array<{ label: string; value: string }>;
    translate: (key: string) => string;
}

export const TaxesFilter: React.FC<ITaxesFilterProps> = ({
    loader,
    filterState,
    setFilterState,
    taxCategories: propTaxCategories,
    translate,
}) => {
    const inputRef = React.useRef<HTMLTextAreaElement>(null);
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const [initialValues, setInitialValues] = useState<IControllerProps>(filterState);

    const taxCategories = useMemo(
        () => propTaxCategories || getTaxCategories(translate),
        [propTaxCategories, translate]
    );

    const taxTypeOptions = useMemo(
        () => taxCategories.map((item) => ({
            enum: item.value,
            label: item.label,
        })),
        [taxCategories]
    );

    useEffect(() => {
        setInitialValues(filterState);
    }, [filterState]);

    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) =>
        setAnchorEl(e.currentTarget);
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleOnDelete = (key: string) => {
        setFilters((prev) => {
            const f = { ...prev };
            delete f[key];
            return f;
        });

        setInitialValues((prev) => ({ ...prev, [key]: undefined as any }));

        setFilterState((prev) => ({
            ...prev,
            [key]: undefined as any,
            page: 0,
        }));
    };

    const filterSettersMap = {
        code: (value: string) => ({
            name: "code",
            value,
            displayValue: value,
            label: translate("code"),
        }),

        taxType: (value: string) => ({
            name: "taxType",
            value,
            displayValue: value,
            label: translate("tax_type"),
        }),
    };

    const handleFilters = (v: any) => {
        const newFilters: Record<string, any> = {};

        Object.entries(v).forEach(([k, val]) => {
            if (val && val !== "" && filterSettersMap[k as keyof typeof filterSettersMap]) {
                const filterObj = filterSettersMap[k as keyof typeof filterSettersMap](
                    val as string
                );
                if (filterObj) newFilters[k] = filterObj;
            }
        });

        setFilters(newFilters);
    };

    const handleChange = (name: string, value: string) =>
        setInitialValues((prev) => ({ ...prev, [name]: value }));

    const { handleSubmit } = useForm({
        defaultValues: {
            code: filterState.code || "",
            taxType: filterState.taxType || "",
        }
    });

    return (
        <FilterMenu
            filters={filters}
            inputRef={inputRef}
            anchorEl={anchorEl}
            open={open}
            handleOpenMenu={handleOpenMenu}
            handleCloseMenu={handleCloseMenu}
            handleOnDelete={handleOnDelete}
            loader={loader}
        >
                    <form
                        onSubmit={handleSubmit(() => {
                            setFilterState((prev) => ({
                                ...prev,
                                ...initialValues,
                                page: 0,
                                rowsPerPage: prev.rowsPerPage ?? 10,
                            }));

                            handleFilters(initialValues);
                            handleCloseMenu();
                        })}
                    >
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    <FilterIcon height={18} width={18} /> {translate("Filters")}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <NewTextField
                                    onChange={(e) => {
                                        handleChange("code", e.target.value);
                                    }}
                                    value={initialValues.code || ""}
                                    label={translate("code")}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <NewAutoCompleteField
                                    value={taxTypeOptions.find(opt => opt.enum === initialValues.taxType) || null}
                                    disabled={loader}
                                    label={translate("tax_type")}
                                    onChange={(_, newValue) => {
                                        if (newValue != null && typeof newValue !== "string") {
                                            handleChange("taxType", newValue?.enum || "");
                                        } else if (newValue === null) {
                                            handleChange("taxType", "");
                                        }
                                    }}
                                    options={taxTypeOptions}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<SearchIcon stroke="white" height={20} width={20} />}
                                type="submit"
                            >
                                {translate("finance_screen.search")}
                            </Button>
                        </Box>
                    </form>
        </FilterMenu>
    );
};